import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getOrderHistory,
  banCustomer,
  voidOrder,
  fullReplacedOrder,
  compensationOrder,
} from "@/api/new-api/order-history/order-history";

export const createCompensationOrder = createAsyncThunk(
  "orderHistory/createCompensationOrder",
  async ({ order_id, data }, { rejectWithValue }) => {
    const res = await compensationOrder(order_id, data);
    if (res.status === "success") return res.data;
    return rejectWithValue(res);
  },
);

export const fetchOrderHistory = createAsyncThunk(
  "orderHistory/fetchOrderHistory",
  async (params, { rejectWithValue }) => {
    const res = await getOrderHistory(params);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  },
);

export const banCustomerById = createAsyncThunk(
  "orderHistory/banCustomerById",
  async (customer_id, { rejectWithValue }) => {
    const res = await banCustomer(customer_id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  },
);

export const voidOrderById = createAsyncThunk(
  "orderHistory/voidOrderById",
  async ({ order_id, data }, { rejectWithValue }) => {
    const res = await voidOrder(order_id, data);
    if (res.status === "success") return { data: res.data, message: res.message };
    return rejectWithValue(res.message);
  },
);

export const fullReplaceOrderById = createAsyncThunk(
  "orderHistory/fullReplaceOrderById",
  async (order_id, { rejectWithValue }) => {
    const res = await fullReplacedOrder(order_id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  },
);

const orderHistorySlice = createSlice({
  name: "orderHistory",
  initialState: {
    orders: [],
    pagination: null,
    statistics: null,
    banCustomerData: null,
    fullReplaceData: null,
    createCompanionData: null,
    createLoading: false,
    getLoading: false,
    updateLoading: false,
    error: null,
  },

  reducers: {
    clearOrderHistoryErrors: (state) => {
      state.error = null;
    },
    updateOrderInHistory: (state, action) => {
      const updatedOrder = action.payload;
      if (!updatedOrder || !updatedOrder.id) return;
      const index = state.orders.findIndex(
        (o) => Number(o.id) === Number(updatedOrder.id)
      );
      if (index !== -1) {
        state.orders[index] = {
          ...state.orders[index],
          ...updatedOrder,
          payment_status:
            updatedOrder.payment_status !== undefined
              ? updatedOrder.payment_status
              : updatedOrder.paymentStatus !== undefined
                ? updatedOrder.paymentStatus
                : state.orders[index].payment_status,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderHistory.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderHistory.fulfilled, (state, action) => {
        state.getLoading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
        state.statistics = action.payload.statistics;
      })
      .addCase(fetchOrderHistory.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })
      .addCase(banCustomerById.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(banCustomerById.fulfilled, (state, action) => {
        state.updateLoading = false;
        const resp = action.payload || {};
        let customer_id;
        let is_banned;
        if (resp.customer_id !== undefined) {
          customer_id = resp.customer_id;
          is_banned = resp.is_banned;
        } else if (resp.data && resp.data.customer_id !== undefined) {
          customer_id = resp.data.customer_id;
          is_banned = resp.data.is_banned;
        }
        if (customer_id !== undefined) {
          state.orders = state.orders.map((o) =>
            o.customer_id === customer_id
              ? { ...o, customer_status: is_banned ? "banned" : "active" }
              : o,
          );
        }
      })
      .addCase(banCustomerById.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(voidOrderById.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(voidOrderById.fulfilled, (state, action) => {
        state.updateLoading = false;
        const responseData = action.payload?.data || {};
        const targetOrderId = responseData.order_id;

        if (targetOrderId !== undefined) {
          state.orders = state.orders.map((order) =>
            order.order_id === targetOrderId || order.id === targetOrderId
              ? {
                ...order,
                is_void: responseData.is_void,
                is_refund: responseData.is_refund,
                refund_type: responseData.refund_type,
                refund_method: responseData.refund_method,
              }
              : order,
          );
        }
      })
      .addCase(voidOrderById.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(fullReplaceOrderById.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(fullReplaceOrderById.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.fullReplaceData = action.payload;
      })
      .addCase(fullReplaceOrderById.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(createCompensationOrder.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createCompensationOrder.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createCompanionData = action.payload;
      })
      .addCase(createCompensationOrder.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearOrderHistoryErrors, updateOrderInHistory } = orderHistorySlice.actions;
export default orderHistorySlice.reducer;
